// src/pages/Login.jsx
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FiArrowLeft,
  FiBookOpen,
  FiCheckCircle,
  FiEye,
  FiEyeOff,
  FiLock,
  FiShield,
  FiUsers,
} from "react-icons/fi";
import { saveToken } from "../auth/auth";
import { useAuth } from "../auth/AuthContext";
import PublicFooter from "../components/PublicFooter";
import {
  confirmRegistration,
  loginUniversal,
  publicAuthErrorMessage,
  requestPasswordReset,
} from "../api/publicAuth";
import "../styles/login.css";

const SETUP_USERNAME_KEY = "la_2fa_setup_username";
const CHALLENGE_USERNAME_KEY = "la_2fa_username";

function Brand() {
  return (
    <Link to="/" className="login-brand" aria-label="LawAfrica home">
      <img
        className="login-brand-logo"
        src="/logo.png"
        alt="LawAfrica — Know. Do. Be More"
      />
    </Link>
  );
}

function Modal({ title, description, children, onClose, locked = false }) {
  return (
    <div
      className="login-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="login-modal-title"
      onMouseDown={(event) => {
        if (!locked && event.target === event.currentTarget) onClose?.();
      }}
    >
      <div className="login-modal" onMouseDown={(event) => event.stopPropagation()}>
        <div className="login-modal-heading">
          <div>
            <h2 id="login-modal-title">{title}</h2>
            {description ? <p>{description}</p> : null}
          </div>
          {!locked ? <button type="button" onClick={onClose} aria-label="Close">×</button> : null}
        </div>
        {children}
      </div>
    </div>
  );
}

function formatGraceDate(value) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString();
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUser } = useAuth();

  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [confirmationLoading, setConfirmationLoading] = useState(false);
  const [confirmationGraceEnd, setConfirmationGraceEnd] = useState(null);

  const [forgotOpen, setForgotOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const resetRef = useRef(null);

  useEffect(() => {
    const stateIdentifier = String(location.state?.identifier || "").trim();
    const stateNotice = String(location.state?.notice || "").trim();

    if (stateIdentifier) setIdentifier(stateIdentifier);
    if (stateNotice) setNotice(stateNotice);

    if (location.state?.openForgotPassword) setForgotOpen(true);

    if (location.state) {
      navigate("/login", { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!forgotOpen) return undefined;
    const timer = window.setTimeout(() => resetRef.current?.focus(), 30);
    return () => window.clearTimeout(timer);
  }, [forgotOpen]);

  function clearMessages() {
    if (error) setError("");
    if (notice) setNotice("");
  }

  function stageTwoFactorSetup() {
    try {
      sessionStorage.setItem(SETUP_USERNAME_KEY, identifier.trim());
    } catch {
      // Router state below remains available even when storage is blocked.
    }

    navigate("/twofactor-setup", {
      replace: true,
      state: {
        username: identifier.trim(),
        password,
        autoFetchSetupToken: true,
      },
    });
  }

  function stageTwoFactorChallenge(data) {
    try {
      sessionStorage.setItem(CHALLENGE_USERNAME_KEY, identifier.trim());
    } catch {
      // Router state below remains available.
    }

    navigate("/twofactor", {
      replace: true,
      state: {
        username: identifier.trim(),
        userId: data?.userId ?? null,
        registrationConfirmationPending: !!data?.registrationConfirmationPending,
        registrationConfirmationGraceEndsAtUtc: data?.registrationConfirmationGraceEndsAtUtc || null,
      },
    });
  }

  async function completeTokenLogin(token) {
    saveToken(token);
    await refreshUser();

    const requestedPath = location.state?.from?.pathname || location.state?.from || "/dashboard";
    navigate(typeof requestedPath === "string" ? requestedPath : "/dashboard", { replace: true });
  }

  async function performLogin() {
    const cleanIdentifier = identifier.trim();
    if (!cleanIdentifier || !password) {
      setError("Enter your email or username and password.");
      return;
    }

    setLoading(true);
    setError("");
    setNotice("");

    try {
      const data = await loginUniversal(cleanIdentifier, password);

      if (data?.requiresRegistrationConfirmation) {
        setConfirmationGraceEnd(data?.registrationConfirmationGraceEndsAtUtc || null);
        setConfirmationOpen(true);
        return;
      }

      if (data?.requires2FASetup) {
        stageTwoFactorSetup();
        return;
      }

      if (data?.requires2FA) {
        stageTwoFactorChallenge(data);
        return;
      }

      const token = data?.token ?? data?.Token;
      if (!token || typeof token !== "string") {
        throw new Error("The sign-in response was incomplete. Please try again.");
      }

      await completeTokenLogin(token);
    } catch (err) {
      const raw = publicAuthErrorMessage(err, "We could not sign you in. Please try again.");
      setError(
        /account locked/i.test(raw)
          ? raw.replace(/^Account locked/i, "Your account is temporarily locked")
          : raw
      );
    } finally {
      setLoading(false);
    }
  }

  async function confirmCurrentRegistration() {
    if (confirmationLoading) return;

    setConfirmationLoading(true);
    setError("");

    try {
      await confirmRegistration(identifier, password);
      setConfirmationOpen(false);
      setNotice("Registration confirmed. Continuing sign in…");

      const data = await loginUniversal(identifier, password);
      if (data?.requires2FASetup) {
        stageTwoFactorSetup();
        return;
      }
      if (data?.requires2FA) {
        stageTwoFactorChallenge(data);
        return;
      }

      const token = data?.token ?? data?.Token;
      if (!token || typeof token !== "string") {
        throw new Error("Registration was confirmed, but sign-in could not continue. Please sign in again.");
      }
      await completeTokenLogin(token);
    } catch (err) {
      setError(publicAuthErrorMessage(err, "We could not confirm this registration. Please try again."));
    } finally {
      setConfirmationLoading(false);
    }
  }

  async function sendReset(event) {
    event.preventDefault();
    const email = resetEmail.trim();
    if (!email || resetLoading) return;

    setResetLoading(true);
    setError("");

    try {
      const data = await requestPasswordReset(email);
      setForgotOpen(false);
      setResetEmail("");
      setNotice(data?.message || "If the account exists, password reset instructions have been sent.");
    } catch (err) {
      setError(publicAuthErrorMessage(err, "Password reset could not be requested. Please try again."));
    } finally {
      setResetLoading(false);
    }
  }

  const graceDate = formatGraceDate(confirmationGraceEnd);

  return (
    <div className="login-page">
      <header className="login-header">
        <Brand />
        <div className="login-header-actions">
          <span>Need a personal account?</span>
          <Link to="/register">Register</Link>
        </div>
      </header>

      <main className="login-shell">
        <section className="login-card" aria-labelledby="login-title">
          <Link to="/" className="login-back-link"><FiArrowLeft aria-hidden="true" /> Back to LawAfrica</Link>

          <div className="login-card-heading">
            <span className="login-eyebrow">One LawAfrica sign in</span>
            <h1 id="login-title">Welcome back</h1>
            <p>Public and institution users sign in here with the credentials assigned to their account.</p>
          </div>

          {notice ? <div className="login-alert login-alert-success" role="status"><FiCheckCircle /> <span>{notice}</span></div> : null}
          {error ? <div className="login-alert login-alert-error" role="alert"><span>{error}</span></div> : null}

          <form
            className="login-form"
            onSubmit={(event) => {
              event.preventDefault();
              performLogin();
            }}
          >
            <label className="login-field">
              <span>Email or username</span>
              <input
                value={identifier}
                onChange={(e) => { setIdentifier(e.target.value); clearMessages(); }}
                autoComplete="username"
                autoFocus
                disabled={loading}
              />
            </label>

            <label className="login-field">
              <span className="login-label-row">
                <span>Password</span>
                <button type="button" onClick={() => setShowPassword((v) => !v)}>
                  {showPassword ? <><FiEyeOff /> Hide</> : <><FiEye /> Show</>}
                </button>
              </span>
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => { setPassword(e.target.value); clearMessages(); }}
                autoComplete="current-password"
                disabled={loading}
              />
            </label>

            <div className="login-form-tools">
              <span />
              <button
                type="button"
                onClick={() => {
                  setResetEmail(identifier.includes("@") ? identifier : "");
                  setForgotOpen(true);
                  setError("");
                }}
              >
                Forgot password?
              </button>
            </div>

            <button className="login-submit" type="submit" disabled={loading}>
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="login-institution-note">
            <FiUsers aria-hidden="true" />
            <div>
              <strong>Institution user?</strong>
              <p>Your Institution Administrator creates your account. You do not self-register or enter an institution code here.</p>
            </div>
          </div>

          <div className="login-card-footer">
            <span>New personal user?</span>
            <Link to="/register">Create a personal account</Link>
          </div>
        </section>

        <aside className="login-context" aria-label="LawAfrica access overview">
          <span className="login-eyebrow">LawAfrica Digital Hub</span>
          <h2>One secure entry point to legal knowledge.</h2>
          <p>
            Your access is resolved after sign-in—personal library for public users, and subscribed resources plus institution services for provisioned institution users.
          </p>

          <div className="login-context-grid">
            <div><FiBookOpen /><span><strong>Legal publications</strong><small>Commentaries, Statutes and Journals.</small></span></div>
            <div><FiShield /><span><strong>Protected access</strong><small>2FA and in-app account confirmation.</small></span></div>
            <div><FiLock /><span><strong>Entitlement aware</strong><small>Access follows your purchases or institution subscription.</small></span></div>
          </div>
        </aside>
      </main>

      <PublicFooter />

      {forgotOpen ? (
        <Modal
          title="Reset your password"
          description="Enter the email address linked to your LawAfrica account."
          onClose={() => { setForgotOpen(false); setResetEmail(""); }}
        >
          <form className="login-modal-form" onSubmit={sendReset}>
            <label className="login-field">
              <span>Email address</span>
              <input
                ref={resetRef}
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                autoComplete="email"
              />
            </label>
            <div className="login-modal-actions">
              <button type="button" className="login-secondary" onClick={() => setForgotOpen(false)}>Cancel</button>
              <button type="submit" className="login-submit login-submit-auto" disabled={resetLoading || !resetEmail.trim()}>
                {resetLoading ? "Sending…" : "Send reset link"}
              </button>
            </div>
          </form>
        </Modal>
      ) : null}

      {confirmationOpen ? (
        <Modal
          title="Confirm your LawAfrica registration"
          description="Complete the in-app account confirmation to continue."
          locked
        >
          <div className="login-confirmation-copy">
            {graceDate ? <p>Your confirmation grace period ends or ended at <strong>{graceDate}</strong>.</p> : null}
            <p>This confirms your LawAfrica account flow. Email inbox verification is not part of this rebuilt registration process.</p>
          </div>
          <button type="button" className="login-submit" onClick={confirmCurrentRegistration} disabled={confirmationLoading}>
            {confirmationLoading ? "Confirming…" : "Confirm and continue"}
          </button>
        </Modal>
      ) : null}
    </div>
  );
}
